import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Info, Database, Mail, Server, RefreshCw } from "lucide-react";

// -----------------------------------------------------------------------------
// SystemInfo — /admin/informazioni
// Read-only snapshot: version, environment, Supabase health, email status.
// -----------------------------------------------------------------------------

const StatusBadge = ({ ok, label, neutral }) => {
  if (neutral) return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700">
      <Info className="w-3.5 h-3.5" />{label}
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${ok ? "bg-[#F0FAF3] text-[#0E7C3D]" : "bg-red-50 text-red-700"}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {label}
    </span>
  );
};

const Row = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-4 py-4 border-b border-[#F1F5F9] last:border-0">
    <div className="w-10 h-10 rounded-xl bg-[#F0FAF3] flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-[#12964A]" /></div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-[#1F2937] mb-1">{title}</div>
      <div className="text-sm text-slate-600">{children}</div>
    </div>
  </div>
);

const SystemInfo = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await adminApi.getSystemInfo(); setInfo(r.data); } catch { setInfo(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#12964A]"><ArrowLeft className="w-4 h-4" /> Pannello</Link>
          <button onClick={load} className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-slate-400 rounded-full px-4 py-1.5 text-sm"><RefreshCw className="w-4 h-4" /> Aggiorna</button>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Amministrazione</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Informazioni sul progetto</h1>
          <p className="text-slate-500 mt-2">Stato del sistema, versione e servizi collegati.</p>
        </div>

        {loading && !info && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#12964A]" /></div>}

        {info && (
          <div className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="system-info-panel">
            <Row icon={Info} title="Applicazione">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-400">Nome:</span> <strong>{info.app.name}</strong></div>
                <div><span className="text-slate-400">Versione:</span> <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{info.app.version}</code></div>
                <div><span className="text-slate-400">Ultimo aggiornamento:</span> {info.app.build_date}</div>
                <div><span className="text-slate-400">Ora server (UTC):</span> <span className="font-mono text-xs">{new Date(info.app.server_time_utc).toLocaleString("it-IT")}</span></div>
              </div>
            </Row>

            <Row icon={Server} title="Ambiente">
              <StatusBadge
                ok={info.environment === "production"}
                neutral={info.environment !== "production"}
                label={info.environment === "production" ? "Production" : "Development"}
              />
            </Row>

            <Row icon={Database} title="Database (Supabase)">
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge ok={info.services.supabase.status === "ok"} label={info.services.supabase.status === "ok" ? "Connesso" : "Errore"} />
                {info.services.supabase.url && <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded truncate max-w-md">{info.services.supabase.url}</code>}
              </div>
              {info.services.supabase.error && <div className="text-xs text-red-600 mt-1">{info.services.supabase.error}</div>}
            </Row>

            <Row icon={Mail} title="Servizio email (Resend)">
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge ok={info.services.email.status === "configured"} label={info.services.email.status === "configured" ? "Invio reale attivo" : "Modalità dry-run"} />
                {info.services.email.sender && <span className="text-xs text-slate-500">Mittente: {info.services.email.sender}</span>}
              </div>
              {info.services.email.status !== "configured" && (
                <div className="text-xs text-slate-500 mt-1">In dry-run le email sono loggate ma non spedite. Per attivarle: configura le variabili <code className="bg-slate-100 px-1 rounded">RESEND_API_KEY</code> e <code className="bg-slate-100 px-1 rounded">SENDER_EMAIL</code>.</div>
              )}
            </Row>
          </div>
        )}
      </main>
    </div>
  );
};

export default SystemInfo;
